// Libs
import React, { useMemo } from 'react'
import PropTypes from 'helpers/proptypes'
import moment from 'moment'
import { businessApi } from 'helpers/api'
import { base64ToBlob } from 'helpers/blob'
import saveAs from 'file-saver'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
// redux
import { getEventOverrides } from 'redux/entities/selectors'
// Components
import { Table, Button, Icon, Label, Popup, Checkbox, List } from 'semantic-ui-react'
import { Link } from 'react-router'
import { FormattedFollowup } from 'components/followups'
import { ConversationModal } from '../../components/conversations/'
// Utils
import { isStatusEditable, computeCancellationDeadline, successfulBookingStatuses } from 'helpers/bookings'
import { getLocalityAndCountry } from 'helpers/places'
import { numbers } from '@vizeat/helpers'

import './Row.css'

const { Row, Cell } = Table

const MUSEMENT_REFERENCE_CODE_REGEX = /^TUI Musement order (MUS\d+)_\d+_rezdy-[a-zA-Z0-9]+$/

function UserCell({ user, hasLeftReview, onOpenHistory }) {
  const { t } = useTranslation()

  return (
    <Cell className={user.ewid ? 'ew' : ''}>
      <Link to={`/users?search=${user.id}`}>{`${user.id} - ${user.firstname} ${user.lastname}`}</Link>
      <br />
      <Popup
        trigger={<Button circular size='mini' icon='history' onClick={() => onOpenHistory(user)} />}
        content={t('Bookings::History')}
        inverted
      />
      <br />
      {hasLeftReview && (
        <span>
          {t('Bookings::(has left a review)')}
          <br />
        </span>
      )}
    </Cell>
  )
}

UserCell.propTypes = {
  user: PropTypes.immutable.record.isRequired,
  hasLeftReview: PropTypes.bool,
  onOpenHistory: PropTypes.func.isRequired,
}

UserCell.defaultProps = {
  hasLeftReview: false,
}

function useBooking(booking) {
  const eventOverride = useSelector((state) => getEventOverrides(state, { id: booking.event.id, date: booking.date }))

  const isEventPrivate =
    booking.event.privatized_at ||
    booking.event.privatized_by ||
    eventOverride.privatized_at ||
    eventOverride.privatized_by
  const eventBeginningTime = eventOverride.begins_at || booking.event.begins_at
  const isRequest =
    ['request_information', 'schedule'].includes(booking.status) &&
    booking.seats > 0 &&
    booking.chosen_dates &&
    (booking.date || booking.chosen_dates.size > 0)

  const eventPrice = useMemo(() => {
    if (!booking.payment || !booking.payment.get('total_paid') || !booking.payment.get('user_currency')) return null
    return numbers.formatIntegerPrice(booking.payment.get('total_paid'), 'en', booking.payment.get('user_currency'))
  }, [booking.payment])

  const bookingDate = useMemo(() => {
    if (booking.date && moment(booking.date).isValid()) return moment(booking.date).format('ll')
    if (booking.get('chosen_dates') && booking.get('chosen_dates').size)
      return booking
        .get('chosen_dates')
        .map((d) => (d && moment(d).isValid ? moment(d).format('ll') : null))
        .filter((d) => !!d)
        .join('\n')
    return ''
  }, [booking])

  const expirationDate = useMemo(() => {
    const lastProposedDate =
      booking.chosen_dates && booking.chosen_dates.size
        ? booking.chosen_dates
            .map((d) => moment(`${d} ${eventBeginningTime}`))
            .sort((d1, d2) => d1.diff(d2))
            .first()
        : null
    const createdAt = moment(booking.created_at).add(3, 'days')
    const dateToConsider = lastProposedDate && lastProposedDate.isBefore(createdAt) ? lastProposedDate : createdAt
    return dateToConsider.format('lll')
  }, [booking.chosen_dates, booking.created_at, eventBeginningTime])

  const cancellationDeadine = useMemo(
    () => computeCancellationDeadline(`${bookingDate.trim()} ${eventBeginningTime}`, booking.cancellation_deadline),
    [booking.cancellation_deadline, bookingDate, eventBeginningTime],
  )

  const bookingFormattedPlace = useMemo(() => getLocalityAndCountry(booking.place), [booking.place])

  const [referenceCode, isMusementReferenceCode] = useMemo(() => {
    const match = booking.reference_code && booking.reference_code.trim().match(MUSEMENT_REFERENCE_CODE_REGEX)
    if (!match) return [booking.reference_code, false]
    return [match[1], true]
  }, [booking.reference_code])

  return {
    isEventPrivate,
    isRequest,
    eventPrice,
    bookingDate,
    expirationDate,
    cancellationDeadine,
    bookingFormattedPlace,
    referenceCode,
    isMusementReferenceCode,
  }
}

export function BookingRow({
  booking,
  onEditStatus,
  addingFollowup,
  changingStatus,
  selected,
  onOpenHistory,
  onSelect,
}) {
  const { t } = useTranslation()
  const {
    isEventPrivate,
    isRequest,
    eventPrice,
    bookingDate,
    expirationDate,
    cancellationDeadine,
    bookingFormattedPlace,
    referenceCode,
    isMusementReferenceCode,
  } = useBooking(booking)
  const shouldDisplayPDFDownloadButton = successfulBookingStatuses.includes(booking.status)

  function handlePDFClick() {
    businessApi
      .post('/api/bookings/pdf', { booking_id: booking.id })
      .then(({ data: { base64 } }) => saveAs(base64ToBlob(base64, 'application/pdf'), `Booking_${booking.id}.pdf`))
  }

  return (
    <Row>
      <Cell className={booking.ewid ? 'ew' : ''}>
        <Link to={`/bookings/${booking.id}`}>{booking.id}</Link>
        {booking.ewid && (
          <i>
            <br />
            {t('Bookings::ew-{{bookingEWId}}', { bookingEWId: booking.ewid })}
          </i>
        )}
        {referenceCode && (
          <span>
            <br />
            {t('Bookings::Code: {{referenceCode}}', { referenceCode })}
            {isMusementReferenceCode && (
              <Popup
                on='click'
                trigger={<Icon circular name='info' style={{ marginLeft: '4px', cursor: 'pointer' }} />}
                content={
                  <span>
                    {booking.reference_code}
                    <Button
                      icon='copy'
                      size='mini'
                      basic
                      style={{ marginLeft: '4px' }}
                      onClick={() => navigator.clipboard.writeText(booking.reference_code)}
                    />
                  </span>
                }
              />
            )}
          </span>
        )}
        {booking.request_id && (
          <span>
            <br />
            <Link to={`/requests?search=${booking.request_id}`}>
              {t('Bookings::R-{{requestId}}', { requestId: booking.request_id })}
            </Link>
          </span>
        )}
      </Cell>

      <Cell>{moment(booking.created_at).format('lll')}</Cell>

      <Cell>{moment(booking.updated_at).format('lll')}</Cell>

      <Cell>{booking.place.country_iso}</Cell>

      <Cell>{bookingFormattedPlace}</Cell>

      <Cell>
        {booking.status}
        &nbsp;
        {changingStatus && <Icon loading name='circle notched' />}
        <br />
        {booking.partner.id && (
          <Label style={{ textAlign: 'center', marginTop: 5 }}>
            {booking.partner.name}
            <br />({booking.partner.organization.name})
          </Label>
        )}
        {isRequest && (
          <Label style={{ textAlign: 'center', marginTop: 5 }}>
            {booking.event.privatized_by ? t('Bookings::PE Request') : t('Bookings::Date Request')}
            <br />
            {booking.date ? t('Bookings::Awaiting Payment') : t('Bookings::Pending Host Approval')}
          </Label>
        )}
        {isEventPrivate && <Label size='small'>{t('Bookings::PRIVATE EVENT')}</Label>}
      </Cell>

      <Cell style={{ whiteSpace: 'pre-line' }}>{bookingDate}</Cell>

      <Cell style={{ whiteSpace: 'pre-line' }}>{expirationDate}</Cell>

      <Cell>{cancellationDeadine}</Cell>

      <UserCell user={booking.user} hasLeftReview={booking.guest_already_left_review} onOpenHistory={onOpenHistory} />

      <UserCell
        user={booking.event.user}
        hasLeftReview={booking.host_already_left_review}
        onOpenHistory={onOpenHistory}
      />

      <Cell className={booking.event.ewid ? 'ew' : ''}>
        <Link to={`/events?search=${booking.event.id}`}>{`${booking.event.id} - ${booking.event.title}`}</Link>
      </Cell>

      <Cell style={{ whiteSpace: 'pre-line' }}>{eventPrice}</Cell>

      <Cell>
        <span>{booking.seats}</span>

        {(!!booking.ew_free_seats || !!booking.host_free_seats) && (
          <Popup
            trigger={<Icon circular name='gift' />}
            content={
              <List>
                <List.Item>{t('Bookings::Total seats: {{seats}}', { seats: booking.seats })}</List.Item>
                <List.Item>
                  {t('Bookings::EW free seats: {{ewFreeSeats}}', { ewFreeSeats: booking.ew_free_seats })}
                </List.Item>
                <List.Item>
                  {t('Bookings::Host free seats: {{hostFreeSeats}}', { hostFreeSeats: booking.host_free_seats })}
                </List.Item>
              </List>
            }
            inverted
          />
        )}
      </Cell>

      <Cell>{booking.coupon_code}</Cell>

      <Cell>
        <FormattedFollowup followups={booking.followups} />
        &nbsp;
        {addingFollowup && <Icon loading name='circle notched' />}
      </Cell>

      <Cell textAlign='center' className='__actionButtons'>
        <Popup
          trigger={
            <Button
              primary
              circular
              onClick={() => onEditStatus(booking)}
              disabled={!isStatusEditable(booking.status)}
              icon='compose'
            />
          }
          content={t('Bookings::Change Status')}
          inverted
        />

        {shouldDisplayPDFDownloadButton && (
          <Popup
            trigger={<Button primary circular icon='file' onClick={handlePDFClick} />}
            content={t('Bookings::Download PDF')}
            inverted
          />
        )}

        <ConversationModal
          conversationId={booking.conversation_id}
          senderId={booking.user.id}
          recipientId={booking.event.user.id}
        />
      </Cell>

      <Cell className='select'>
        <Checkbox onChange={(e, { checked }) => onSelect(checked, booking.id)} checked={selected} />
      </Cell>
    </Row>
  )
}

BookingRow.propTypes = {
  booking: PropTypes.immutable.record.isRequired,
  onEditStatus: PropTypes.func.isRequired,
  onOpenHistory: PropTypes.func.isRequired,
  changingStatus: PropTypes.bool,
  addingFollowup: PropTypes.bool,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
}

BookingRow.defaultProps = {
  changingStatus: false,
  addingFollowup: false,
  selected: false,
}
